import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { useCommunityTopicStore } from '@/stores/communityTopicStore';
import { createCommunityPost, updateCommunityPost } from '@/service/api/community';
import { uploadFile } from '@/utils/upload';
import { ConfirmModal } from '@/components/common';

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `idempotency-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

interface PostModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** 可选：需要编辑的动态数据 (存在即进入编辑模式) */
  initialData?: Api.Community.PostResponse | null;
}

interface UploadedMediaItem {
  id: string;
  file: File;
  url: string;
  media_type: 'IMAGE' | 'VIDEO';
  asset_id?: string;
  uploading: boolean;
  error?: string;
}

export default function PostModal({ open, onClose, onSuccess, initialData }: PostModalProps) {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const { topics, fetchTopics } = useCommunityTopicStore();

  const isEditMode = Boolean(initialData && initialData.id);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTopicId, setSelectedTopicId] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  const [mediaItems, setMediaItems] = useState<UploadedMediaItem[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  // 控制二次确认弹窗
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  // 判断是否有用户输入/编辑内容
  const hasInput =
    title.trim().length > 0 ||
    description.trim().length > 0 ||
    selectedTopicId.trim().length > 0 ||
    mediaItems.length > 0 ||
    tags.length > 0 ||
    tagInput.trim().length > 0;

  useEffect(() => {
    if (open) {
      fetchTopics().catch(() => { });
      if (initialData) {
        setTitle(initialData.title || '');
        setDescription(initialData.content || '');
        if (initialData.topics && initialData.topics.length > 0) {
          setSelectedTopicId(initialData.topics[0].id || '');
        } else {
          setSelectedTopicId('');
        }
        if (initialData.media_assets && initialData.media_assets.length > 0) {
          const existingMedia: UploadedMediaItem[] = initialData.media_assets.map((m, idx) => ({
            id: m.asset_id || `media-${idx}`,
            file: new File([], ''),
            url: m.access_url || '',
            media_type: m.media_type?.toUpperCase() === 'VIDEO' ? 'VIDEO' : 'IMAGE',
            asset_id: m.asset_id,
            uploading: false,
          }));
          setMediaItems(existingMedia);
        } else {
          setMediaItems([]);
        }
      } else {
        resetForm();
      }
      setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimating(true));
      });
    } else {
      setAnimating(false);
      const timer = setTimeout(() => {
        setVisible(false);
        resetForm();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open, initialData, fetchTopics]);

  // 快捷键 ESC 关闭拦截逻辑
  useEffect(() => {
    if (!open || !visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        requestClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, visible, hasInput, submitting, submitted, showConfirmClose]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSelectedTopicId('');
    setTags([]);
    setTagInput('');

    mediaItems.forEach((item) => {
      if (item.url.startsWith('blob:')) {
        URL.revokeObjectURL(item.url);
      }
    });
    setMediaItems([]);

    setSubmitted(false);
    setErrorMsg(null);
    setSubmitting(false);
    setShowConfirmClose(false);
  };

  /** 请求关闭弹窗，如果有内容输入则显示二次确认 */
  const requestClose = () => {
    if (submitting || submitted) return;

    if (hasInput) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  /** 确定放弃修改并关闭弹窗 */
  const handleConfirmDiscard = () => {
    setShowConfirmClose(false);
    onClose();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const maxAllowed = 9 - mediaItems.length;
    const filesToProcess = selectedFiles.slice(0, maxAllowed);

    for (const file of filesToProcess) {
      const isVideo = file.type.startsWith('video/');
      const media_type: 'IMAGE' | 'VIDEO' = isVideo ? 'VIDEO' : 'IMAGE';
      const tempId = Math.random().toString(36).slice(2, 9);
      const previewUrl = URL.createObjectURL(file);

      const newItem: UploadedMediaItem = {
        id: tempId,
        file,
        url: previewUrl,
        media_type,
        uploading: true,
      };

      setMediaItems((prev) => [...prev, newItem]);

      try {
        const res = await uploadFile(file, 'COMMUNITY');
        const assetId = String(res.asset_id || res.upload_asset_id || '');

        // 自动提取类型
        const detectedType: 'IMAGE' | 'VIDEO' = res.file_type === 'VIDEO' || isVideo ? 'VIDEO' : 'IMAGE';

        setMediaItems((prev) =>
          prev.map((item) =>
            item.id === tempId
              ? {
                ...item,
                asset_id: assetId,
                media_type: detectedType,
                url: res.file_url || previewUrl,
                uploading: false,
              }
              : item
          )
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : '文件上传失败';
        setMediaItems((prev) =>
          prev.map((item) => (item.id === tempId ? { ...item, uploading: false, error: msg } : item))
        );
      }
    }

    e.target.value = '';
  };

  const handleRemoveMediaItem = (id: string) => {
    setMediaItems((prev) => {
      const item = prev.find((i) => i.id === id);
      if (item?.url.startsWith('blob:')) {
        URL.revokeObjectURL(item.url);
      }
      return prev.filter((i) => i.id !== id);
    });
  };

  const isUploadingMedia = mediaItems.some((m) => m.uploading);

  const canSubmit =
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    selectedTopicId.trim().length > 0 &&
    !isUploadingMedia;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoggedIn) {
      onClose();
      navigate('/auth');
      return;
    }
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    setErrorMsg(null);

    try {
      const validMediaAssets: Api.Community.MediaAssetRequest[] = mediaItems
        .filter((m) => m.asset_id && !m.uploading && !m.error)
        .map((m, idx) => ({
          asset_id: m.asset_id!,
          media_type: m.media_type,
          sort_no: idx + 1,
        }));

      const postData: Api.Community.PostRequest = {
        title: title.trim(),
        content: description.trim(),
        submit: true, // 提交发布/审核
        topic_ids: selectedTopicId ? [selectedTopicId] : undefined,
        media_assets: validMediaAssets.length > 0 ? validMediaAssets : undefined,
      };

      if (isEditMode && initialData?.id) {
        const idempotencyKey = generateIdempotencyKey();
        await updateCommunityPost(initialData.id, postData, idempotencyKey);
      } else {
        await createCommunityPost(postData);
      }

      setSubmitted(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1200);
    } catch (err) {
      const message = err instanceof Error ? err.message : (isEditMode ? '修改动态失败，请稍后重试' : '发布帖子失败，请稍后重试');
      setErrorMsg(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      requestClose();
    }
  };

  if (!visible) return null;

  return (
    <>
      <div
      onClick={handleOverlayClick}
      className={`fixed inset-0 z-[9999] flex items-start justify-center pt-[10vh] px-4 transition-all duration-300 ${animating ? 'bg-foreground-950/40 backdrop-blur-sm' : 'bg-transparent'
        }`}
    >
      <div
        className={`w-full max-w-[640px] bg-white rounded-2xl shadow-2xl transition-all duration-300 overflow-hidden relative ${animating ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'
          }`}
      >
        {submitted ? (
          <div className="p-10 text-center">
            <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-check-line text-3xl text-primary-600"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">
              {isEditMode ? '修改成功！' : '发布成功！'}
            </h2>
            <p className="text-sm text-foreground-500">
              {isEditMode ? '你的动态修改已保存' : '你的帖子已提交发布到社区广场'}
            </p>
          </div>
        ) : (
          <>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200/70">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className={`${isEditMode ? 'ri-edit-line' : 'ri-quill-pen-line'} text-primary-600`}></i>
                </div>
                <h2 className="font-heading font-bold text-foreground-950">
                  {isEditMode ? '编辑动态' : '发布帖子'}
                </h2>
              </div>
              <button
                type="button"
                onClick={requestClose}
                disabled={submitting}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-50"
              >
                <i className="ri-close-line text-lg text-foreground-500"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div className="max-h-[65vh] overflow-y-auto px-6 py-5">
              <form id="post-form" onSubmit={handleSubmit}>
                <div className="space-y-5">
                  {/* Topic from Store */}
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-2">
                      话题分类 <span className="text-primary-500">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {topics.map((t) => (
                        <button
                          key={t.id || t.name}
                          type="button"
                          onClick={() => setSelectedTopicId(t.id || '')}
                          className={`text-xs px-3.5 py-1.5 rounded-full whitespace-nowrap transition-all cursor-pointer ${selectedTopicId === t.id
                            ? 'bg-gradient-to-r from-primary-500 to-primary-300 text-white font-medium shadow-sm'
                            : 'border border-background-300 bg-white text-foreground-500 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50'
                            }`}
                        >
                          {t.name}
                        </button>
                      ))}
                      {topics.length === 0 && (
                        <span className="text-xs text-foreground-400">暂无话题可选</span>
                      )}
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                      帖子标题 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="清晰概括你想分享的内容..."
                      maxLength={100}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                    <p className="text-xs text-foreground-400 mt-1 text-right">{title.length}/100</p>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                      帖子内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="详细描述你的经验、思路或作品细节..."
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">{description.length}/500</p>
                  </div>

                  {/* Media Upload */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-medium text-foreground-700">
                        媒体附件（支持图片/视频）
                      </label>
                      <span className="text-xs text-foreground-400">{mediaItems.length}/9</span>
                    </div>

                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {mediaItems.map((item) => (
                        <div
                          key={item.id}
                          className="relative group rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-square flex items-center justify-center"
                        >
                          {item.media_type === 'IMAGE' ? (
                            <img src={item.url} alt="预览" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-white p-2">
                              <i className="ri-video-line text-2xl mb-1"></i>
                              <span className="text-[10px] text-gray-300 truncate max-w-full">
                                {item.file.name}
                              </span>
                            </div>
                          )}

                          {/* Uploading Spinner */}
                          {item.uploading && (
                            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white">
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-1"></div>
                              <span className="text-[10px]">上传中...</span>
                            </div>
                          )}

                          {/* Error Badge */}
                          {item.error && (
                            <div className="absolute inset-0 bg-red-900/80 flex flex-col items-center justify-center text-white p-1 text-center">
                              <i className="ri-error-warning-line text-lg text-red-300 mb-0.5"></i>
                              <span className="text-[9px] line-clamp-2">{item.error}</span>
                            </div>
                          )}

                          {/* Remove Button */}
                          {!item.uploading && (
                            <button
                              type="button"
                              onClick={() => handleRemoveMediaItem(item.id)}
                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:bg-red-600"
                            >
                              <i className="ri-close-line text-xs"></i>
                            </button>
                          )}

                          {/* Type Badge */}
                          <span className="absolute bottom-1 left-1 text-[9px] px-1.5 py-0.5 rounded bg-black/60 text-white font-medium">
                            {item.media_type === 'IMAGE' ? '图片' : '视频'}
                          </span>
                        </div>
                      ))}

                      {/* Upload Trigger Button */}
                      {mediaItems.length < 9 && (
                        <label className="border-2 border-dashed border-gray-300 hover:border-primary-400 rounded-xl aspect-square flex flex-col items-center justify-center cursor-pointer transition-colors text-foreground-400 hover:text-primary-500 bg-gray-50 hover:bg-primary-50/30">
                          <input
                            type="file"
                            accept="image/*,video/*"
                            multiple
                            className="hidden"
                            onChange={handleFileChange}
                            disabled={submitting}
                          />
                          <i className="ri-upload-cloud-2-line text-2xl mb-1"></i>
                          <span className="text-[11px] font-medium">上传附件</span>
                          <span className="text-[9px] text-foreground-400 mt-0.5">图片/视频</span>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Error Alert */}
                  {errorMsg && (
                    <div className="p-3 rounded-lg bg-red-50 text-red-600 text-xs flex items-center gap-2">
                      <i className="ri-error-warning-line text-sm"></i>
                      <span>{errorMsg}</span>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200/70 flex gap-3">
              <button
                type="button"
                onClick={requestClose}
                disabled={submitting}
                className="flex-1 py-2.5 rounded-lg border border-gray-300/60 text-foreground-600 text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="submit"
                form="post-form"
                disabled={!canSubmit || submitting}
                className="flex-1 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:brightness-95 active:brightness-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 shadow-sm"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{isEditMode ? '保存中...' : '发布中...'}</span>
                  </>
                ) : (
                  <>
                    <i className={`${isEditMode ? 'ri-save-line' : 'ri-send-plane-line'} text-base`}></i>
                    <span>{isEditMode ? '保存修改' : '发布帖子'}</span>
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>

    {/* 二次确认放弃弹窗 (通用 ConfirmModal 组件，自带进场与出场淡入淡出/缩放动画) */}
    <ConfirmModal
      open={showConfirmClose}
      title="放弃编辑该帖子？"
      content="检测到你有尚未保存的修改，离开或关闭弹窗后已输入的内容将不会被保存。"
      type="danger"
      confirmText="放弃并关闭"
      cancelText="继续编辑"
      onConfirm={handleConfirmDiscard}
      onCancel={() => setShowConfirmClose(false)}
    />
  </>
  );
}